import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { apiClient, API_ENDPOINTS, API_HOST_BASE } from '../utils/apiUtils';
import { Container, Card, Row, Col, Badge, Button, Alert, Table } from 'react-bootstrap';
import { getEvaluationReportStatusVariant, getEvaluationReportStatusDisplayText } from '../utils/evaluationStatus';
import EvaluationReportModal from './EvaluationReportModal';
import './ReportModule.css';

const EvaluationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [evaluation, setEvaluation] = useState(null);
  const [project, setProject] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);

  // Check permissions
  const canViewEvaluations = user && ['Extension Coordinator', 'Extension Head', 'GAD', 'Vice Chancellor for Administration and Finance', 'Vice Chancellor for Research, Development and Extension Services', 'Chancellor', 'Admin'].includes(user.role);
  const isExtensionCoordinator = user && user.role === 'Extension Coordinator';

  useEffect(() => {
    if (!canViewEvaluations) {
      navigate('/reports');
      return;
    }

    fetchEvaluationData();
  }, [canViewEvaluations, navigate, id]);

  const fetchEvaluationData = async () => {
    try {
      setLoading(true);
      
      // Fetch evaluation data
      const evaluationResponse = await apiClient.get(`${API_ENDPOINTS.EVALUATIONS}/${id}`);
      const evaluationData = evaluationResponse.data;
      setEvaluation(evaluationData);

      // Fetch project data
      const projectResponse = await apiClient.get(`${API_ENDPOINTS.PROJECTS}/${evaluationData.project_id}`);
      console.log('EvaluationView - Project data received:', projectResponse.data);
      console.log('EvaluationView - Project monitoring_evaluation:', projectResponse.data.monitoring_evaluation);
      setProject(projectResponse.data);

      // Fetch evaluation report for this project
      try {
        const reportResponse = await apiClient.get(`${API_ENDPOINTS.REPORTS}?project_id=${evaluationData.project_id}`);
        if (reportResponse.data && reportResponse.data.length > 0) {
          const reportData = reportResponse.data[0];
          // Ensure photos are parsed correctly
          if (reportData.photos) {
            try {
              if (typeof reportData.photos === 'string') {
                reportData.photos = JSON.parse(reportData.photos);
              }
              console.log('EvaluationView - Loaded photos:', reportData.photos.length);
              // Check if photos are in row-based structure
              const isRowBased = Array.isArray(reportData.photos) && reportData.photos.length > 0 && 
                                 (reportData.photos[0].row_id !== undefined || reportData.photos[0].frameCount !== undefined);
              
              if (isRowBased) {
                // Handle row-based structure
                reportData.photos = reportData.photos.map(row => {
                  if (row.photos && Array.isArray(row.photos)) {
                    row.photos = row.photos.map(photo => {
                      if (!photo.url) {
                        photo.url = `/uploads/report-photos/${photo.filename}`;
                      }
                      return photo;
                    });
                  }
                  return row;
                });
              } else {
                // Handle flat array structure (legacy)
                reportData.photos = reportData.photos.map(photo => {
                  if (!photo.url) {
                    photo.url = `/uploads/report-photos/${photo.filename}`;
                  }
                  return photo;
                });
              }
            } catch (e) {
              console.error('Error parsing photos in EvaluationView:', e);
              reportData.photos = [];
            }
          }
          setReport(reportData);
        }
      } catch (reportErr) {
        console.log('No evaluation report found for this project');
        setReport(null);
      }

    } catch (err) {
      console.error('Error fetching evaluation data:', err);
      setError('Failed to load evaluation data');
    } finally {
      setLoading(false);
    }
  };

  const getDecisionVariant = (decision) => {
    switch (decision) {
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'danger';
      case 'Needs Revision':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const getDecisionIcon = (decision) => {
    switch (decision) {
      case 'Approved':
        return 'bi-check-circle-fill';
      case 'Rejected':
        return 'bi-x-circle-fill';
      case 'Needs Revision':
        return 'bi-exclamation-triangle-fill';
      default:
        return 'bi-question-circle-fill';
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'Ongoing':
        return 'primary';
      case 'Not Started':
        return 'secondary';
      case 'Cancelled':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const asList = (str) => {
    try {
      return str ? str.split(',').map(s => s.trim()).filter(s => s) : [];
    } catch { 
      return []; 
    }
  };

  const renderMonitoringEvaluationTable = (monitoringData) => {
    console.log('EvaluationView - renderMonitoringEvaluationTable called with:', monitoringData);
    if (!monitoringData) return <div>—</div>;
    
    let tableData;
    try {
      tableData = typeof monitoringData === 'string' ? JSON.parse(monitoringData) : monitoringData;
      console.log('EvaluationView - Parsed table data:', tableData);
      
      // Debug responsible fields specifically
      const objectiveTypes = ['impact', 'outcome', 'output', 'activities', 'input'];
      objectiveTypes.forEach(type => {
        if (tableData[type]) {
          console.log(`EvaluationView - ${type} responsible:`, `"${tableData[type].responsible}"`);
        }
      });
    } catch {
      return <div style={{ whiteSpace: 'pre-wrap' }}>{monitoringData}</div>;
    }

    if (!tableData || typeof tableData !== 'object') {
      return <div>—</div>;
    }

    const objectiveTypes = [
      { key: 'impact', label: 'Impact-' },
      { key: 'outcome', label: 'Outcome-' },
      { key: 'output', label: 'Output-' },
      { key: 'activities', label: 'Activities-' },
      { key: 'input', label: 'Input-' }
    ];

    return (
      <div className="table-responsive">
        <table className="table table-bordered me-view-table" style={{ fontSize: '0.85rem' }}>
          <thead className="table-light">
            <tr>
              <th style={{ width: '8%', padding: '8px', textAlign: 'center' }}>Objectives</th>
              <th style={{ width: '12%', padding: '8px' }}>Performance Indicators</th>
              <th style={{ width: '10%', padding: '8px' }}>Baseline Data</th>
              <th style={{ width: '10%', padding: '8px' }}>Performance Target</th>
              <th style={{ width: '10%', padding: '8px' }}>Data Source</th>
              <th style={{ width: '10%', padding: '8px' }}>Collection Method</th>
              <th style={{ width: '10%', padding: '8px' }}>Frequency of Data Collection</th>
              <th style={{ width: '18%', padding: '8px' }}>Office/Persons Responsible</th>
            </tr>
          </thead>
          <tbody>
            {objectiveTypes.map(({ key, label }) => {
              const rowData = tableData[key] || {};
              return (
                <tr key={key} style={{ verticalAlign: 'top' }}>
                  <td className="fw-bold bg-light" style={{ padding: '12px 8px', fontSize: '0.9rem', textAlign: 'center' }}>
                    {label}
                  </td>
                  <td style={{ padding: '8px', fontSize: '0.85rem' }}>
                    {rowData.performance_indicators || '—'}
                  </td>
                  <td style={{ padding: '8px', fontSize: '0.85rem' }}>
                    {rowData.baseline_data || '—'}
                  </td>
                  <td style={{ padding: '8px', fontSize: '0.85rem' }}>
                    {rowData.performance_target || '—'}
                  </td>
                  <td style={{ padding: '8px', fontSize: '0.85rem' }}>
                    {rowData.data_source || '—'}
                  </td>
                  <td style={{ padding: '8px', fontSize: '0.85rem' }}>
                    {rowData.collection_method || '—'}
                  </td>
                  <td style={{ padding: '8px', fontSize: '0.85rem' }}>
                    {rowData.frequency || '—'}
                  </td>
                  <td style={{ padding: '8px', fontSize: '0.85rem' }}>
                    {rowData.responsible !== null && rowData.responsible !== undefined ? rowData.responsible : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (!canViewEvaluations) {
    return null;
  }

  if (loading) {
    return (
      <div className="container-fluid px-3 px-md-4 py-3">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading evaluation details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid px-3 px-md-4 py-3">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button variant="outline-danger" onClick={() => navigate('/reports')}>
              Back to Reports
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  if (!evaluation || !project) {
    return (
      <div className="container-fluid px-3 px-md-4 py-3">
        <Alert variant="warning">
          <Alert.Heading>Evaluation Not Found</Alert.Heading>
          <p>The requested evaluation could not be found.</p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button variant="outline-warning" onClick={() => navigate('/reports')}>
              Back to Reports
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Evaluation Details</h2>
            <p className="text-muted mb-0">Project: {project.title}</p>
          </div>
          <div className="d-flex gap-2">
            <Button 
              variant="secondary" 
              size="sm"
              style={{ minWidth: '40px', height: '32px' }}
              onClick={() => navigate('/reports')}
              title="Back"
            >
              <i className="bi bi-arrow-left"></i>
            </Button>
            {(user.user_id === evaluation.evaluator_id || user.role === 'Admin') && (
              <Button 
                variant="outline-primary"
                size="sm"
                style={{ minWidth: '40px', height: '32px' }}
                onClick={() => setShowEditModal(true)}
                title="Edit"
              >
                <i className="bi bi-pencil"></i>
              </Button>
            )}
          </div>
        </div>

        {/* Evaluation Information */}
        <Card className="shadow-sm mb-4">
          <Card.Header className="bg-primary text-white">
            <h5 className="mb-0">
              <i className="bi bi-clipboard-check me-2"></i>
              Evaluation Information
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <strong>Evaluator:</strong>
                  <div>{evaluation.evaluator_name}</div>
                  <small className="text-muted">{evaluation.evaluator_role}</small>
                </div>
                <div className="mb-3">
                  <strong>Decision:</strong>
                  <div>
                    <Badge bg={getDecisionVariant(evaluation.decision)} className="decision-badge">
                      <i className={`${getDecisionIcon(evaluation.decision)} me-1`}></i>
                      {evaluation.decision}
                    </Badge>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <strong>Total Score:</strong>
                  <div>{evaluation.total_score} / 100</div>
                </div>
                <div className="mb-3">
                  <strong>Score Percentage:</strong>
                  <div>{evaluation.score_percentage}%</div>
                </div>
                <div className="mb-3">
                  <strong>Evaluation Date:</strong>
                  <div>{new Date(evaluation.created_at).toLocaleDateString()}</div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col>
                <div className="mb-3">
                  <strong>Feedback:</strong>
                  <div className="mt-2 p-3 bg-light rounded">
                    {evaluation.feedback || 'No feedback provided'}
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Project Information */}
        <Card className="shadow-sm mb-4">
          <Card.Header className="bg-info text-white">
            <h5 className="mb-0">
              <i className="bi bi-folder me-2"></i>
              Project Information
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <strong>Project Title:</strong>
                  <div>{project.title}</div>
                </div>
                <div className="mb-3">
                  <strong>Status:</strong>
                  <div>
                    <Badge bg={getStatusVariant(project.status)} className="status-badge">
                      {project.status}
                    </Badge>
                  </div>
                </div>
                <div className="mb-3">
                  <strong>Coordinator:</strong>
                  <div>{project.coordinator_name}</div>
                </div>
                <div className="mb-3">
                  <strong>Request/Initiative Type:</strong>
                  <div>{project.request_initiative_type || '—'}</div>
                </div>
                <div className="mb-3">
                  <strong>Location:</strong>
                  <div>{project.location || '—'}</div>
                </div>
                <div className="mb-3">
                  <strong>Duration:</strong>
                  <div>{project.duration || '—'}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                <strong>Start Date:</strong>
                <div>{project.start_date ? new Date(project.start_date).toLocaleDateString() : '—'}</div>
              </div>
              <div className="mb-3">
                <strong>End Date:</strong>
                <div>{project.end_date ? new Date(project.end_date).toLocaleDateString() : '—'}</div>
                </div>
                <div className="mb-3">
                  <strong>Extension Agenda:</strong>
                  <div>{asList(project.extension_agenda).join(', ') || '—'}</div>
                </div>
                <div className="mb-3">
                  <strong>SDG Goals:</strong>
                  <div>{asList(project.sdg_goals).join(', ') || '—'}</div>
                </div>
                <div className="mb-3">
                  <strong>Total Cost:</strong>
                  <div>{project.total_cost ? `₱${parseFloat(project.total_cost).toLocaleString()}` : '—'}</div>
                </div>
                <div className="mb-3">
                  <strong>Fund Source:</strong>
                  <div>{project.fund_source || '—'}</div>
                </div>
              </Col>
            </Row>
            
            {/* Offices and Organizations Involved */}
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <strong>Offices/Organizations Involved:</strong>
                  <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{project.offices_involved || '—'}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <strong>Programs Involved:</strong>
                  <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{project.programs_involved || '—'}</div>
                </div>
              </Col>
            </Row>

            {/* Partner Agencies and Beneficiaries */}
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <strong>Partner Agencies:</strong>
                  <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{project.partner_agencies || '—'}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <strong>Beneficiaries:</strong>
                  <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{project.beneficiaries || '—'}</div>
                </div>
              </Col>
            </Row>

            {/* Project Leadership */}
            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <strong>Project Leader:</strong>
                  <div className="mt-1">{project.project_leader_name || '—'}</div>
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <strong>Assistant Project Leaders:</strong>
                  <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{project.assistant_project_leader_names ? 
                    (() => {
                      try {
                        const names = typeof project.assistant_project_leader_names === 'string' && project.assistant_project_leader_names.trim().startsWith('[')
                          ? JSON.parse(project.assistant_project_leader_names)
                          : project.assistant_project_leader_names;
                        return Array.isArray(names) ? names.join(', ') : names;
                      } catch {
                        return project.assistant_project_leader_names;
                      }
                    })() : '—'}</div>
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <strong>Coordinators:</strong>
                  <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{project.coordinator_names ? 
                    (() => {
                      try {
                        const names = typeof project.coordinator_names === 'string' && project.coordinator_names.trim().startsWith('[')
                          ? JSON.parse(project.coordinator_names)
                          : project.coordinator_names;
                        return Array.isArray(names) ? names.join(', ') : names;
                      } catch {
                        return project.coordinator_names;
                      }
                    })() : '—'}</div>
                </div>
              </Col>
            </Row>

            {/* Narrative Sections */}
            {[
              ['rationale','Rationale'],
              ['objectives','Objectives (General and Specific)'],
              ['expected_output','Expected Output'],
              ['strategies_methods','Description, Strategies and Methods'],
              ['financial_plan_details','Financial Plan'],
              ['functional_relationships','Functional Relationships']
            ].map(([key, label]) => (
              <Row key={key}>
                <Col>
                  <div className="mb-3">
                    <strong>{label}:</strong>
                    <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{project[key] || '—'}</div>
                  </div>
                </Col>
              </Row>
            ))}

            {project.remarks && (
              <Row>
                <Col>
                  <div className="mb-3">
                    <strong>Remarks:</strong>
                    <div className="mt-2 p-3 bg-light rounded">
                      {project.remarks}
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {project.narrative_of_activity && (
              <Row>
                <Col>
                  <div className="mb-3">
                    <strong>Narrative of Activity:</strong>
                    <div className="mt-2 p-3 bg-light rounded">
                      {project.narrative_of_activity}
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {(project.monitoring_evaluation || project.monitoring_evaluation_plan) && (
              <Row>
                <Col>
                  <div className="mb-3">
                    <strong>Monitoring & Evaluation Plan:</strong>
                    <div className="mt-2">
                      {renderMonitoringEvaluationTable(project.monitoring_evaluation || project.monitoring_evaluation_plan)}
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {project.sustainability_plan && (
              <Row>
                <Col>
                  <div className="mb-3">
                    <strong>Sustainability Plan:</strong>
                    <div className="mt-2 p-3 bg-light rounded">
                      {project.sustainability_plan}
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>

        {/* Evaluation Report Information */}
        {report ? (
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0">
                <i className="bi bi-clipboard-data me-2"></i>
                Evaluation Report
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Report Created By:</strong>
                    <div>{report.created_by_name}</div>
                    <small className="text-muted">{report.created_by_department}</small>
                  </div>
                  <div className="mb-3">
                    <strong>Report Status:</strong>
                    <div>
                      <Badge bg={getEvaluationReportStatusVariant(report.status || 'Pending')}>
                        {getEvaluationReportStatusDisplayText(report.status || 'Pending')}
                      </Badge>
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Report Date:</strong>
                    <div>{new Date(report.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Participant Type:</strong>
                    <div>{report.participant_type || '—'}</div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Male BatStateU Participants:</strong>
                    <div>{report.male_batstateu_participants || 0}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Female BatStateU Participants:</strong>
                    <div>{report.female_batstateu_participants || 0}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Male Other Participants:</strong>
                    <div>{report.male_other_participants || 0}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Female Other Participants:</strong>
                    <div>{report.female_other_participants || 0}</div>
                  </div>
                </Col>
              </Row>

              {report.narrative_of_activity && (
                <Row>
                  <Col>
                    <div className="mb-3">
                      <strong>Narrative of Activity:</strong>
                      <div className="mt-2 p-3 bg-light rounded">
                        {report.narrative_of_activity}
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Activity Rating Table */}
              <Row>
                <Col>
                  <div className="mb-3">
                    <strong>Activity Rating:</strong>
                    <div className="mt-2">
                      <Table responsive bordered size="sm">
                        <thead className="table-light">
                          <tr>
                            <th>Rating</th>
                            <th>BatStateU</th>
                            <th>Other Institution</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td><strong>Excellent</strong></td>
                            <td>{report.activity_excellent_batstateu || 0}</td>
                            <td>{report.activity_excellent_other || 0}</td>
                            <td>{(report.activity_excellent_batstateu || 0) + (report.activity_excellent_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Very Satisfactory</strong></td>
                            <td>{report.activity_very_satisfactory_batstateu || 0}</td>
                            <td>{report.activity_very_satisfactory_other || 0}</td>
                            <td>{(report.activity_very_satisfactory_batstateu || 0) + (report.activity_very_satisfactory_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Satisfactory</strong></td>
                            <td>{report.activity_satisfactory_batstateu || 0}</td>
                            <td>{report.activity_satisfactory_other || 0}</td>
                            <td>{(report.activity_satisfactory_batstateu || 0) + (report.activity_satisfactory_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Fair</strong></td>
                            <td>{report.activity_fair_batstateu || 0}</td>
                            <td>{report.activity_fair_other || 0}</td>
                            <td>{(report.activity_fair_batstateu || 0) + (report.activity_fair_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Poor</strong></td>
                            <td>{report.activity_poor_batstateu || 0}</td>
                            <td>{report.activity_poor_other || 0}</td>
                            <td>{(report.activity_poor_batstateu || 0) + (report.activity_poor_other || 0)}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Timeliness Rating Table */}
              <Row>
                <Col>
                  <div className="mb-3">
                    <strong>Timeliness Rating:</strong>
                    <div className="mt-2">
                      <Table responsive bordered size="sm">
                        <thead className="table-light">
                          <tr>
                            <th>Rating</th>
                            <th>BatStateU</th>
                            <th>Other Institution</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td><strong>Excellent</strong></td>
                            <td>{report.timeliness_excellent_batstateu || 0}</td>
                            <td>{report.timeliness_excellent_other || 0}</td>
                            <td>{(report.timeliness_excellent_batstateu || 0) + (report.timeliness_excellent_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Very Satisfactory</strong></td>
                            <td>{report.timeliness_very_satisfactory_batstateu || 0}</td>
                            <td>{report.timeliness_very_satisfactory_other || 0}</td>
                            <td>{(report.timeliness_very_satisfactory_batstateu || 0) + (report.timeliness_very_satisfactory_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Satisfactory</strong></td>
                            <td>{report.timeliness_satisfactory_batstateu || 0}</td>
                            <td>{report.timeliness_satisfactory_other || 0}</td>
                            <td>{(report.timeliness_satisfactory_batstateu || 0) + (report.timeliness_satisfactory_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Fair</strong></td>
                            <td>{report.timeliness_fair_batstateu || 0}</td>
                            <td>{report.timeliness_fair_other || 0}</td>
                            <td>{(report.timeliness_fair_batstateu || 0) + (report.timeliness_fair_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Poor</strong></td>
                            <td>{report.timeliness_poor_batstateu || 0}</td>
                            <td>{report.timeliness_poor_other || 0}</td>
                            <td>{(report.timeliness_poor_batstateu || 0) + (report.timeliness_poor_other || 0)}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Photos - Row-based Display */}
              {report.photos && report.photos.length > 0 && (() => {
                // Check if photos are in row-based structure
                const isRowBased = report.photos[0]?.row_id !== undefined || report.photos[0]?.frameCount !== undefined;
                const photoRows = isRowBased ? report.photos : [];
                
                // If not row-based, convert to rows for display
                const displayRows = isRowBased ? photoRows : (() => {
                  const rows = [];
                  for (let i = 0; i < report.photos.length; i += 2) {
                    rows.push({
                      row_id: `row_${i}`,
                      frameCount: Math.min(2, report.photos.length - i),
                      photos: report.photos.slice(i, i + 2),
                      caption: ''
                    });
                  }
                  return rows;
                })();
                
                return (
                  <Row>
                    <Col>
                      <div className="mb-3">
                        <strong>Activity Photos:</strong>
                        <div className="mt-2">
                          {displayRows.map((row, rowIndex) => (
                            <div key={row.row_id || rowIndex} className="mb-4">
                              <Row>
                                {row.photos && Array.isArray(row.photos) && row.photos.length > 0 && row.photos.map((photo, photoIndex) => {
                                  if (!photo || (!photo.url && !photo.filename)) {
                                    console.warn('Invalid photo data:', photo);
                                    return null;
                                  }
                                  
                                  const photoUrl = (() => {
                                    let url = photo.url;
                                    if (!url && photo.filename) {
                                      url = `/uploads/report-photos/${photo.filename}`;
                                    }
                                    if (url && !url.startsWith('http')) {
                                      url = `${API_HOST_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
                                    }
                                    if (process.env.NODE_ENV !== 'production') {
                                      console.log(`Photo URL for row ${rowIndex + 1}, photo ${photoIndex + 1}:`, url);
                                    }
                                    return url;
                                  })();
                                  
                                  return (
                                    <Col md={row.frameCount === 1 ? 12 : 6} key={photo.photo_id || `photo-${rowIndex}-${photoIndex}`} className="mb-3">
                                      <div className="position-relative">
                                        <img
                                          src={photoUrl}
                                          alt={`Row ${rowIndex + 1} Photo ${photoIndex + 1}`}
                                          className="img-fluid rounded shadow-sm"
                                          style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                                          onError={(e) => {
                                            console.error('Photo failed to load:', e.target.src);
                                            console.error('Photo data:', photo);
                                            e.target.style.display = 'none';
                                          }}
                                          onLoad={() => {
                                            if (process.env.NODE_ENV !== 'production') {
                                              console.log('Photo loaded successfully:', photoUrl);
                                            }
                                          }}
                                        />
                                        {/* Per-Photo Caption - displayed directly under each photo */}
                                        {row.captionType === 'photo' && photo.caption && (
                                          <div className="mt-2 text-center">
                                            <small className="text-dark fst-italic">{photo.caption}</small>
                                          </div>
                                        )}
                                      </div>
                                    </Col>
                                  );
                                })}
                              </Row>
                              {/* Row Caption - displayed below the entire row */}
                              {row.captionType === 'row' && row.caption && (
                                <div className="mt-2 text-center">
                                  <small className="text-dark fst-italic">{row.caption}</small>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>
                  </Row>
                );
              })()}
            </Card.Body>
          </Card>
        ) : (
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-warning text-dark">
              <h5 className="mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                No Evaluation Report Found
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="mb-0">No evaluation report has been submitted for this project yet.</p>
            </Card.Body>
          </Card>
        )}

      {/* Edit Evaluation Report Modal */}
      {report && (
        <EvaluationReportModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          reportId={report.report_id}
          onSuccess={() => {
            // Reload evaluation data after successful edit
            fetchEvaluationData();
            setShowEditModal(false);
          }}
        />
      )}
    </div>
  );
};

export default EvaluationView;
